import { assertDefined, debugCommand, debugLog } from 'shared/utils/debug';
import type PlanogramPoint from 'shared/utils/PlanogramPoint';

import type { TextureCache } from './TextureCache';
import type { TileMap } from './TileMap';
import { TRANSPARENT_TILE } from './TileMap';
import type PhysicalTextures from './PhysicalTextures';
import { iterateLevels, computeImageMapSize } from './helpers';
import {
  ExtraImageData,
  FrameMessage,
  ImageData,
  ImageId,
  InitiateMessage,
  OperationOutcome,
  OperationResult,
  RemoveImageMessage,
  UpdateImageMessage,
  WorkerMessage,
} from './interfaces';
import { WorkerMessageType } from './interfaces';
import MultiSet from './MultiSet';
import { receiveSerializedMessages, sendSerializedMessage } from '../utils/serialization.util';
import { cleanSlot, LoaderContext, Operation, SlotMap } from './operations';
import { CorsWorker as Worker } from '../utils/CorsWorker';
import { isCanceledError } from '../errors/Canceled';

// at least one slot is required to run any operations
const OPERATION_RUNTIME_SLOTS = 1;
// time without tasks that has to pass to consider tile loader out of work
const IDLE_WINDOW = 100;

async function runOperation(
  context: LoaderContext,
  operation: Operation,
  usedSlotsLimit: number,
): Promise<boolean> {
  let result = false;
  try {
    await operation.preload(context);
    if (operation.usedSlots > usedSlotsLimit) {
      operation.cancel(context);
    } else {
      operation.execute(context);
      result = true;
    }
  } catch (e) {
    if (!isCanceledError(e)) {
      console.warn('Failed operation', e.toString());
      try {
        operation.cancel(context);
      } catch (e) {
        console.warn('Failed to cancel operation', e.toString());
      }
    }
  }
  return result;
}

// TODO: clean up chunkId vs chunk index vs chunk coords mess

export default class TileLoader implements LoaderContext {
  images: Map<ImageId, ImageData> = new Map();
  // TODO: somehow the SlotMap can be missing entries for chunks present in OperationSelectionWorker.currentState, and seemingly with tiles loaded and displayed
  slotMap: SlotMap = new SlotMap(); // level => (chunkId => slotId)
  private worker: globalThis.Worker;

  private affectedChunks: MultiSet<number> = new MultiSet();
  private runningOperations: Set<Operation> = new Set();
  private reservedSlots: number = 0;
  private timeOutOfWork: number | undefined;

  constructor(
    public tileMap: TileMap,
    public textureCache: TextureCache,
    public physicalTextures: PhysicalTextures,
    public cdnUrl: string,
    loadingLimit: number,
    unloadedLevelBias: number,
    blacklistDuration: number,
    targetPixelRatio: number,
  ) {
    debugCommand('lodLoader', () => this);
    const workerLoader = new Worker(new URL('./OperationSelectionWorker.ts', import.meta.url), {
      type: 'module',
      name: 'lod-worker',
    });
    this.worker = workerLoader.createWorker();
    receiveSerializedMessages<WorkerMessage>(this.worker, message => this.receiveMessage(message));
    const initiateMessage: InitiateMessage = {
      type: WorkerMessageType.Initiate,
      loadingLimit,
      blacklistDuration,
      unloadedLevelBias,
      targetPixelRatio,
      tileMapResolution: this.tileMap.resolution,
    };
    sendSerializedMessage(this.worker, initiateMessage);
  }

  private updateOutOfWork() {
    this.timeOutOfWork = new Date().getTime() + IDLE_WINDOW;
  }

  private receiveMessage(message: WorkerMessage) {
    switch (message.type) {
      case WorkerMessageType.OperationTarget: {
        this.updateOutOfWork();
        const { operation, id } = message;
        assertDefined(operation, 'Received undefined operation from the worker');
        const availableSlots = this.physicalTextures.freeSlots - this.reservedSlots;
        const tooFewSlots = availableSlots < operation.reservedSlots;
        const collision = operation.affectedChunkIds.some(it => this.affectedChunks.has(it));
        if (tooFewSlots || collision) {
          debugLog(
            `Canceled operation ${operation} due to ${
              tooFewSlots ? 'insufficient slots' : 'collision'
            }`,
          );
          operation.cancel(this);
          this.reportOperationOutcome(id, OperationOutcome.Cancelation);
          break;
        }
        const maxSlotsToUse = availableSlots - OPERATION_RUNTIME_SLOTS;
        this.runningOperations.add(operation);
        this.reservedSlots += operation.reservedSlots;
        operation.affectedChunkIds.forEach(chunkId => this.affectedChunks.add(chunkId));
        runOperation(this, operation, maxSlotsToUse)
          .then(success => {
            this.reportOperationOutcome(
              id,
              success ? OperationOutcome.Success : OperationOutcome.Failure,
            );
            if (!success) debugLog(`Failed operation ${operation}`);
            else debugLog(`Succesful operation ${operation}`);
          })
          .finally(() => {
            this.runningOperations.delete(operation);
            this.reservedSlots -= operation.reservedSlots;
            operation.affectedChunkIds.forEach(chunkId => this.affectedChunks.remove(chunkId));
          });
        break;
      }
      case WorkerMessageType.CleanSlots: {
        const image = this.images.get(message.imageId);
        assertDefined(image, 'Attemp to clear an already deleted image');
        Object.entries(message.chunkMasks).forEach(it => {
          const [chunkIdKey, mask] = it;
          const chunkId = parseInt(chunkIdKey);
          iterateLevels(
            mask,
            // TODO: does this cancel all loading tiles?
            level => cleanSlot(this, image, level, chunkId),
          );
        });
        this.images.delete(message.imageId);
      }
    }
  }

  private reportOperationOutcome(id: number, outcome: OperationOutcome) {
    const message: OperationResult = {
      type: WorkerMessageType.OperationResult,
      id,
      outcome,
    };
    sendSerializedMessage(this.worker, message);
  }

  addImage(image: ImageData) {
    const message: UpdateImageMessage = {
      type: WorkerMessageType.UpdateImage,
      data: image,
    };
    sendSerializedMessage(this.worker, message);
    this.tileMap.storeTileLocation(
      image.mapPosition,
      computeImageMapSize(image.lodData),
      TRANSPARENT_TILE,
    );
    this.images.set(image.id, image);
  }

  updateImage(id: ImageId, extraData: ExtraImageData) {
    const image = this.images.get(id);
    if (image === undefined) return;
    const newImage = {
      ...image,
      extraData: {
        ...extraData,
        position: extraData.position.clone(),
      },
    };
    const message: UpdateImageMessage = {
      type: WorkerMessageType.UpdateImage,
      data: newImage,
    };
    sendSerializedMessage(this.worker, message);
    this.images.set(id, newImage);
  }

  removeImage(image: ImageData) {
    const message: RemoveImageMessage = {
      type: WorkerMessageType.RemoveImage,
      id: image.id,
    };
    sendSerializedMessage(this.worker, message);
  }

  update(focusPoint: PlanogramPoint, pixelRatio: number): boolean {
    if (this.images.size === 0) return true;
    const message: FrameMessage = {
      type: WorkerMessageType.Frame,
      focusPointX: focusPoint.x,
      focusPointY: focusPoint.y,
      pixelRatio: pixelRatio,
      slots: this.physicalTextures.freeSlots - this.reservedSlots,
    };
    sendSerializedMessage(this.worker, message);
    return this.timeOutOfWork !== undefined && this.timeOutOfWork <= new Date().getTime(); // TODO: return true when worker confirms no work?
  }

  dispose() {
    this.worker.terminate();
  }
}
